import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, IconButton, Tooltip,
  DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import commonStore from "../../Store/marco/commonStore";
import organizationalStructureStore from "../../Store/marco/organizationalStructureStore";
import { organizationListExcelAPI as api, saveExcelDownload } from "../organization-list-excel-api";
import HistoryIcon from "@mui/icons-material/History";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";

export const canImportOrganizationList = (validation) =>
  validation?.status === "ok" ||
  (validation?.status === "error" && Number(validation?.summary?.update) > 0);

const BUSY_LABELS = {
  checking: "Checking active imports", validating: "Validating file", importing: "Importing...",
  exporting: "Exporting...", history: "Loading import history", report: "Downloading error report",
};

export const OrganizationListExcelModal = observer(({ onClose, showHistory = false }) => {
  const { t, i18n } = useTranslation("common");
  const [busy, setBusy] = useState("checking");
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(null);
  const [checked, setChecked] = useState(false);
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const fileInputRef = useRef(null);
  const permitted = ["1", "2"].includes(String(commonStore.roleId));
  const currentLanguage = i18n?.language || commonStore.lang || "en";
  const lang = currentLanguage === "tw" ? "zh_tw" : currentLanguage;
  const uploadDisabled = Boolean(busy) || !checked || Boolean(locked);

  useEffect(() => {
    let active = true;
    api.active().then(data => { if (active) { setLocked(data); setChecked(true); } })
      .catch(err => { if (active) setError(err.response?.data?.message || err.message); })
      .finally(() => { if (active) setBusy(""); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!showHistory) return undefined;
    let active = true;
    api.history().then(response => {
      if (active) setHistory(Array.isArray(response) ? response : response?.items || []);
    }).catch(err => { if (active) setError(err.response?.data?.message || err.message); });
    return () => { active = false; };
  }, [showHistory]);

  const run = async (name, action) => {
    setBusy(name);
    setError("");
    try { await action(); }
    catch (err) { setError(err.response?.data?.message || err.message || t("Request failed")); }
    finally { setBusy(""); }
  };

  const checkLock = async () => {
    const active = await api.active();
    setChecked(true);
    setLocked(active);
    return !active;
  };

  const selectFile = (selected) => {
    if (!selected || uploadDisabled || !permitted) return;
    setFile(selected || null);
    setValidation(null);
    setResult(null);
    if (!selected) return;
    if (!/\.xlsx$/i.test(selected.name)) { setError(t("Only .xlsx files are accepted")); return; }
    run("validating", async () => {
      if (!(await checkLock())) return;
      setValidation(await api.validate(selected));
    });
  };

  const chooseFile = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    selectFile(selected);
  };

  const submit = () => run("importing", async () => {
    if (!permitted || !file || !canImportOrganizationList(validation) || result) return;
    if (!(await checkLock())) return;
    const data = await api.import(file);
    setResult(data);
    setHistory(null);
    try {
      const companies = await commonStore._handleSetCompanyList(commonStore.userId);
      organizationalStructureStore._handleSetCompanyListData("fullList", companies);
    } catch (_) { setError(t("Import completed, but refreshing companies failed. Please reload the page.")); }
  });

  const report = (jobId) => run("report", async () => {
    saveExcelDownload(await api.errorReport(jobId, lang), "organization_list_errors.pdf");
  });

  const details = (title, rows) => rows?.length > 0 && <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle2">{t(title)}</Typography>
    <Box sx={{ overflowX: "auto", maxHeight: 220 }}>
      <Table size="small" stickyHeader>
        <TableHead><TableRow>{["Row", "Field", "Message"].map(label => <TableCell key={label}>{t(label)}</TableCell>)}</TableRow></TableHead>
        <TableBody>{rows.map((row, index) => <TableRow key={index}>
          <TableCell>{row.row}</TableCell><TableCell>{row.field || row.fields?.join(", ") || "—"}</TableCell>
          <TableCell>{t(row.message)}</TableCell>
        </TableRow>)}</TableBody>
      </Table>
    </Box>
  </Box>;

  const data = result || validation;
  const summary = data?.summary;
  const importDescription = t("Import updates Name, Country and Address of existing companies. Rows cannot be added or deleted, and Organization Level / Parent Entity are read-only here.");
  const importDescriptionLead = t("Import updates Name, Country and Address of existing companies.");
  return <Dialog open fullWidth maxWidth="sm" aria-labelledby="organization-list-excel-title"
    PaperProps={{ sx: { borderRadius: "6px", color: "#101828", m: { xs: 1, sm: 4 }, width: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
      "& .MuiButton-root": { fontSize: 12, fontWeight: 700, borderRadius: "3px", minHeight: 35 },
      "& .MuiButton-outlined": { color: "#1645ff", borderColor: "#cbd5eb" } } }} onClose={busy ? undefined : onClose}>
    <DialogTitle id="organization-list-excel-title" sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.5, py: 1.75, borderBottom: "1px solid #e6e9ef" }}>
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", bgcolor: "#0b8070", color: "#fff", borderRadius: "4px", width: 32, height: 23, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>OL</Box>
      <Typography component="span" sx={{ flex: 1, fontSize: 16, fontWeight: 500 }}>{t("Import and export")}</Typography>
      <Tooltip title={t("View import history")}><IconButton aria-label={t("View import history")} size="small" disabled={Boolean(busy)} onClick={() => run("history", async () => {
        const response = await api.history();
        setHistory(Array.isArray(response) ? response : response?.items || response?.jobs || []);
      })}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
      <IconButton aria-label={t("Close")} size="small" disabled={Boolean(busy)} onClick={onClose} sx={{ color: "#61769b" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
    </DialogTitle>
    <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2.5, pt: 1.75, pb: 2 }}>
      <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={Boolean(busy) || !checked || Boolean(locked)} onClick={() => run("exporting", async () => {
        saveExcelDownload(await api.export(lang), "organization_list.xlsx");
      })}>{t("Export")}</Button>
    </Box>
    <DialogContent sx={{ px: 2.5, pt: 0, pb: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}
        {!checked && <Button onClick={() => run("checking", checkLock)}>{t("Retry")}</Button>}
      </Alert>}
      {locked && <Alert severity="warning" sx={{ mb: 2 }}>
        {t("An import is in progress. All modules are locked.")} {locked.fileName}
        <Button disabled={Boolean(busy)} onClick={() => run("checking", checkLock)}>{t("Refresh")}</Button>
      </Alert>}
      {permitted && <Box>
        <Typography sx={{ fontSize: 12, mb: 1 }}>{t("New file upload")}</Typography>
        <input ref={fileInputRef} hidden aria-label={t("Organization List Excel file")} type="file" accept=".xlsx" onChange={chooseFile} disabled={uploadDisabled} />
        <Box component="button" type="button" disabled={uploadDisabled} onClick={() => fileInputRef.current?.click()}
          onDragOver={event => event.preventDefault()}
          onDrop={event => { event.preventDefault(); selectFile(event.dataTransfer.files?.[0]); }}
          sx={{ width: "100%", minHeight: 130, p: 2.5, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5,
            border: "1px dashed #c2ceeb", borderRadius: "3px", bgcolor: "#fafbfc", color: "inherit", fontFamily: "inherit", cursor: "pointer",
            "&:hover:not(:disabled), &:focus-visible": { bgcolor: "#f2f5ff", borderColor: "#1645ff" },
            "&:focus-visible": { outline: "2px solid #1645ff", outlineOffset: 2 },
            "&:disabled": { opacity: 0.55, cursor: "not-allowed" } }}>
          <FileUploadIcon sx={{ color: "#667085", fontSize: 27, mb: 0.75 }} />
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, overflowWrap: "anywhere", maxWidth: "100%" }}>{file?.name || t("Choose a .xlsx file to import")}</Typography>
          <Typography component="span" sx={{ fontSize: 12, color: "#98a2b3" }}>{t("Only .xlsx files are accepted")}</Typography>
        </Box>
      </Box>}
      <Box sx={{ mt: 1.25, py: 1.25, px: 1.5, bgcolor: "#f5f7ff", borderLeft: "3px solid #1645ff", borderRadius: "0 5px 5px 0", color: "#1645ff" }}>
        <Typography sx={{ fontSize: 12, lineHeight: 1.7 }}>
          {importDescription.startsWith(importDescriptionLead) ? <>
            <Box component="strong">{importDescriptionLead}</Box>{importDescription.slice(importDescriptionLead.length)}
          </> : importDescription}
        </Typography>
      </Box>
      {busy && <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}><CircularProgress size={20} />{t(BUSY_LABELS[busy])}</Box>}
      {summary && <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1">{t(result ? "Import result" : "Validation result")}</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          {Object.entries(summary).map(([key, value]) => <Chip key={key} label={`${t(key)}: ${value ?? 0}`} variant="outlined" />)}
        </Box>
      </Box>}
      {validation?.status === "failed" && !result && <Alert severity="error" sx={{ mt: 2 }}>{t("This file cannot be imported. Please export a new template.")}</Alert>}
      {validation?.status === "error" && !result && <Alert severity="warning" sx={{ mt: 2 }}>{t("Rows with errors will be skipped. Only valid rows marked Modify will be imported.")}</Alert>}
      {result && <Alert severity={Number(summary?.errors) ? "warning" : "success"} sx={{ mt: 2 }}>{t("Import completed")}</Alert>}
      {details("Errors", data?.errors)}
      {details("Modified rows not marked Modify", data?.notMarked)}
      {details("Read-only changes ignored", data?.readOnlyIgnored)}
      {result?.reportUrl && result?.jobId && <Button disabled={Boolean(busy)} onClick={() => report(result.jobId)}>{t("Download error report")}</Button>}
      {history !== null && <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1">{t("History")} — OL</Typography>
        {!history.length && <Typography>{t("No import history")}</Typography>}
        {history.map(item => <Box key={item.jobId || item.id} sx={{ py: 1, borderBottom: "1px solid #eee", overflowWrap: "anywhere" }}>
          <Typography>{item.fileName} · {item.status} · {item.completedAt || item.startedAt}</Typography>
          {item.reportUrl && (item.jobId || item.id) && <Button disabled={Boolean(busy)} onClick={() => report(item.jobId || item.id)}>{t("Download error report")}</Button>}
        </Box>)}
      </Box>}
    </DialogContent>
    <DialogActions sx={{ minHeight: 64, px: 2.5, py: 1.5, borderTop: "1px solid #e6e9ef" }}>
      {permitted && <Button variant="contained" disabled={Boolean(busy) || !checked || Boolean(locked) || Boolean(result) || !canImportOrganizationList(validation)} onClick={submit}>{t("Import")}</Button>}
      <Button disabled={Boolean(busy)} onClick={onClose} sx={{ color: "#101828", minWidth: 40 }}>{t("Close")}</Button>
    </DialogActions>
  </Dialog>;
});
